import { useCreatorDashboardAccess } from "MVVM/Hooks/useCreatorDashboardAccess";
import {
  HiCog,
  HiLink,
  HiChatAlt2,
  HiLogout,
  HiQuestionMarkCircle,
} from "react-icons/hi";
import { BsPin, BsPinFill } from "react-icons/bs";
import { MdInsights, MdVideoSettings } from "react-icons/md";
import { FaDiscord } from "react-icons/fa";

import { useUser } from "MVVM/Hooks/useUser";
import { NAV_LINKS } from "MVVM/Components/Sidebar/utils";
import { useSubscriptionInfo } from "MVVM/Hooks/useHasProAccess";
import { ClipQuotaBar } from "MVVM/Components/ClipQuotaBar";
import { URLS } from "MVVM/Utilities/Maps";
import { DISCORD_INVITE_URL, SUPPORT_URL, personalLinks } from "../links";
import { useShortcuts } from "../useShortcuts";
import {
  MenuAction,
  MenuCount,
  MenuDivider,
  MenuIcon,
  MenuLabel,
  MenuNewBadge,
  MenuLink,
  MenuPending,
  MenuList,
  PersonalRows,
  MenuPanel,
  MenuPlanFooter,
  MenuPlanCta,
  PinButton,
  QuotaSlot,
  SectionLabel,
  ShortcutRow,
} from "./styledComponents";
import { useActiveClips } from "./useActiveClips";
import { SetupChecklist } from "./SetupChecklist";
import type { useSetupChecklist } from "MVVM/Hooks/useSetupChecklist";

export const Panel = ({
  panelId,
  checklist,
  onNavigate,
  onOpenFeedback,
}: {
  panelId: string;
  checklist: ReturnType<typeof useSetupChecklist>;
  onNavigate: () => void;
  onOpenFeedback: () => void;
}) => {
  const { allstarUser, logout } = useUser();
  const { isProOrHigher } = useSubscriptionInfo();
  const canAccessCreatorDashboard = useCreatorDashboardAccess();
  const { isPinned, toggle } = useShortcuts();

  const pendingClips = useActiveClips().length;

  const user = allstarUser?.user;
  const email = allstarUser?.decoded?.sub;

  return (
    <MenuPanel id={panelId} role="region" aria-label="Your Allstar">
      {/* Setup takes this slot until it's finished or hidden. */}
      {!checklist.showIndicator && (
        <QuotaSlot>
          <ClipQuotaBar clipQuota={user?.clipQuota} userEmail={email} compact />
        </QuotaSlot>
      )}

      {checklist.showIndicator && (
        <SetupChecklist checklist={checklist} onNavigate={onNavigate} />
      )}

      <MenuList>
        <SectionLabel>My Shortcuts</SectionLabel>
        {/* Listed most important first here, the reverse of the strip, which is
            ordered so its leading items are the ones a narrow viewport drops. */}
        <PersonalRows>
          {[...personalLinks(user?.profile?.username || user?.username)]
            .reverse()
            .map((link) => {
              const Icon = link.icon;
              const pinned = isPinned(link.key);

              return (
                <ShortcutRow key={link.key}>
                  <MenuLink to={link.to} onClick={onNavigate}>
                    <MenuIcon>
                      <Icon />
                    </MenuIcon>
                    <MenuLabel>{link.label}</MenuLabel>
                    {link.key === "library" &&
                      (pendingClips ? (
                        <MenuPending>{pendingClips} rendering</MenuPending>
                      ) : (
                        Number(user?.stats?.totalClips) > 0 && (
                          <MenuCount>{user?.stats?.totalClips}</MenuCount>
                        )
                      ))}
                  </MenuLink>
                  <PinButton
                    type="button"
                    $pinned={pinned}
                    aria-pressed={pinned}
                    aria-label={`${pinned ? "Unpin" : "Pin"} ${
                      link.label
                    } in the top bar`}
                    title={
                      pinned ? "Unpin from the top bar" : "Pin to the top bar"
                    }
                    onClick={() => toggle(link.key)}
                  >
                    {pinned ? <BsPinFill /> : <BsPin />}
                  </PinButton>
                </ShortcutRow>
              );
            })}
        </PersonalRows>
        <MenuLink to="/referrals" onClick={onNavigate}>
          <MenuIcon>
            <HiLink />
          </MenuIcon>
          <MenuLabel>Affiliate Program</MenuLabel>
          <MenuNewBadge>New</MenuNewBadge>
        </MenuLink>
        {canAccessCreatorDashboard && (
          <MenuLink to={NAV_LINKS.dashboard} onClick={onNavigate}>
            <MenuIcon>
              <MdInsights />
            </MenuIcon>
            <MenuLabel>Creator Dashboard</MenuLabel>
            <MenuNewBadge>New</MenuNewBadge>
          </MenuLink>
        )}
      </MenuList>

      <MenuDivider />

      <MenuList>
        <MenuLink to="/accountsettings" onClick={onNavigate}>
          <MenuIcon>
            <HiCog />
          </MenuIcon>
          <MenuLabel>Account Settings</MenuLabel>
        </MenuLink>

        <MenuLink to={URLS.CLIP_SETTINGS} onClick={onNavigate}>
          <MenuIcon>
            <MdVideoSettings />
          </MenuIcon>
          <MenuLabel>Clip Settings</MenuLabel>
        </MenuLink>

        <MenuLink to="/connectedaccounts" onClick={onNavigate}>
          <MenuIcon>
            <HiLink />
          </MenuIcon>
          <MenuLabel>Connected Accounts</MenuLabel>
        </MenuLink>
      </MenuList>

      <MenuDivider />

      <MenuList>
        <MenuAction type="button" onClick={onOpenFeedback}>
          <MenuIcon>
            <HiChatAlt2 />
          </MenuIcon>
          <MenuLabel>Leave us Feedback</MenuLabel>
        </MenuAction>

        <MenuAction
          as="a"
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
        >
          <MenuIcon>
            <HiQuestionMarkCircle />
          </MenuIcon>
          <MenuLabel>Support</MenuLabel>
        </MenuAction>

        <MenuAction
          as="a"
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
        >
          <MenuIcon>
            <FaDiscord />
          </MenuIcon>
          <MenuLabel>Join our Discord</MenuLabel>
        </MenuAction>

        <MenuAction type="button" onClick={() => logout("Site nav user menu.")}>
          <MenuIcon>
            <HiLogout />
          </MenuIcon>
          <MenuLabel>Log out</MenuLabel>
        </MenuAction>
      </MenuList>

      {/* Free accounts had no upgrade route anywhere in the panel: the footer
          only rendered for subscribers, and the bar's trial button is hidden
          once you have used your trial. The identity panel spec puts an upgrade
          CTA here as state 2's most prominent element.

          Tier-agnostic wording on purpose. The subscription overhaul renamed
          the tiers to Basic, Standard and Unlimited, so the spec's "Upgrade to
          Pro" no longer names a plan we sell, and the pricing page does the
          selling anyway. */}
      <MenuPlanFooter>
        <MenuPlanCta
          to={isProOrHigher ? "/subscriptions" : URLS.UPGRADE}
          onClick={onNavigate}
        >
          {isProOrHigher ? "Manage plan" : "Upgrade your plan"}
        </MenuPlanCta>
      </MenuPlanFooter>
    </MenuPanel>
  );
};
